import { cloneElement, CSSProperties, isValidElement, ReactElement, ReactNode, useEffect, useState } from 'react';
import { IPageLink } from '@vivli/shared/infrastructure/interface';
import { HorizontalNavTabView } from './horizontal-nav-tab.view';
import { Size, Color } from '@vivli/shared/theme';

export interface HorizontalNavProps {
    links: IPageLink[];
    activeLink?: IPageLink;
    onTabClick?: (link: IPageLink) => void;
    isLoading?: boolean;
    topTabSet?: boolean;
    activeUrl?: string | undefined;
    // Optional interceptor run before a tab switch; return false (or a Promise resolving false) to
    // veto the switch (nothing changes). Used to confirm before leaving during a file upload.
    beforeTabChange?: (link: IPageLink) => boolean | Promise<boolean>;
}

export const HorizontalNavView = ({
    links,
    activeLink,
    activeUrl: activeUrlProp,
    onTabClick,
    isLoading,
    topTabSet,
    beforeTabChange
}: HorizontalNavProps) => {
    const useTopStyle = topTabSet;
    const hasLinks = !!links?.length;

    //for top tabs need to compose the column set as it differs for
    //different users
    let columnSpecifier = 'auto '.repeat(links?.length ?? 0).trim();

    if (links?.some((link) => (link as any).containedComponent)) {
        columnSpecifier = columnSpecifier.substring(0, columnSpecifier.lastIndexOf(' '));
        columnSpecifier += ' auto';
    }

    const containerStyle: CSSProperties = {
        width: '100%',
        backgroundColor: useTopStyle ? Color.VIVLI_DARK_BLUE : '#fff',
        height: useTopStyle ? Size.HEADER_HEIGHT : '53px',
        maxHeight: useTopStyle ? Size.HEADER_HEIGHT : '53px',
        minHeight: useTopStyle ? Size.HEADER_HEIGHT : '53px',
        display: 'grid',
        gap: useTopStyle ? '0px' : '12px',
        gridTemplateColumns: useTopStyle ? columnSpecifier : 'repeat(auto-fit, minmax(100px, 285px))',
        justifyContent: useTopStyle ? 'end' : 'start',
        marginLeft: useTopStyle ? 'auto' : '0'
    };

    const [currentActiveLink, setCurrentActiveLink] = useState<IPageLink | undefined>(activeLink);
    const [currentActiveUrl, setCurrentActiveUrl] = useState<string | undefined>(activeUrlProp ?? activeLink?.url);
    const [autoOpenContainedKey, setAutoOpenContainedKey] = useState<string | undefined>();

    const getLinkKey = (link: IPageLink) => link?.url ?? link?.title;

    useEffect(() => {
        setCurrentActiveLink(activeLink);
        setCurrentActiveUrl(activeUrlProp ?? activeLink?.url);

        const nextActiveKey = activeLink ? getLinkKey(activeLink) : undefined;

        setAutoOpenContainedKey((currentKey) => {
            if (!currentKey) {
                return undefined;
            }

            return currentKey === nextActiveKey ? currentKey : undefined;
        });
    }, [activeLink, activeUrlProp]);

    const handleOnTabClick = async (link: IPageLink) => {
        // Allow a consumer to veto the switch (e.g. confirm before leaving during an upload). Gating
        // here, at the click origin, means a veto changes neither the highlight nor the body.
        if (beforeTabChange && !(await beforeTabChange(link))) {
            return;
        }

        setCurrentActiveLink(link);
        setCurrentActiveUrl(link?.url);
        setAutoOpenContainedKey((link as any)?.containedComponent ? getLinkKey(link) : undefined);
        onTabClick?.(link);
    };

    const handleCloseContained = () => {
        setCurrentActiveLink(undefined);
        setCurrentActiveUrl(undefined);
        setAutoOpenContainedKey(undefined);
    };

    return (
        <div style={containerStyle}>
            {hasLinks &&
                links
                    .filter((link) => !link.hidden)
                    .map((link) => {
                        const isActive = currentActiveLink === link || currentActiveUrl === link?.url;
                        const contained = (link as any)?.containedComponent;
                        const shouldAutoOpenContained = autoOpenContainedKey === getLinkKey(link);

                        const tab = (
                            <HorizontalNavTabView
                                title={link?.title}
                                url={link?.url}
                                active={isActive}
                                onTabClick={() => handleOnTabClick(link)}
                                isDisabled={isLoading && activeLink?.url !== link.url}
                                isLoading={isLoading && activeLink?.url !== link.url}
                                count={link?.count}
                                useTopStyle={useTopStyle}
                                containedComponent={contained}
                            />
                        );

                        if (!contained) {
                            return <div key={link?.url}>{tab}</div>;
                        }

                        const Contained = contained as
                            | React.ComponentType<{
                                  link: IPageLink;
                                  close?: () => void;
                                  defaultOpen?: boolean;
                              }>
                            | ReactNode;

                        return (
                            <div key={link?.url} style={{ position: 'relative' }}>
                                {!isActive && tab}
                                {isActive && (
                                    <div
                                        style={{
                                            top: useTopStyle ? 0 : 53,
                                            height: '100%',
                                            right: 0,
                                            zIndex: 1000,
                                            width: 'max-content',
                                            backgroundColor: Color.VIVLI_ACTIVE_BLUE
                                        }}
                                    >
                                        {typeof Contained === 'function' ? (
                                            <Contained link={link} close={handleCloseContained} defaultOpen={shouldAutoOpenContained} />
                                        ) : isValidElement(contained) ? (
                                            cloneElement(contained as ReactElement<{ defaultOpen?: boolean }>, {
                                                defaultOpen: shouldAutoOpenContained
                                            })
                                        ) : (
                                            Contained
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
        </div>
    );
};
